import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { FiltersControls, Pagination, Spinner, Table, ButtonAction, Modal } from 'components';
import {
  loadIpmaProjectRoleApplicationsCreator,
  setIpmaProjectRoleApplicationPageCreator,
  setIpmaProjectRoleApplicationPerPageCreator,
  acceptIpmaProjectRoleApplicationCreator,
  declineIpmaProjectRoleApplicationCreator,
} from 'store/actionsCreators';
import { isLoading } from 'helpers';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FlexColumn } from '../Application.styled';

const OnHold = ({
  applications,
  page,
  pages,
  perPage,
  loading,
  loadApplications,
  setPage,
  setPerPage,
  acceptApplication,
  declineApplication,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [acceptConfirmModal, setAcceptConfirmModal] = useState(null);
  const [declineConfirmModal, setDeclineConfirmModal] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [perPage, setPage]);

  useEffect(() => {
    loadApplications({ status: 'on_hold' }, id);
  }, [perPage, page, loadApplications]);

  const handleAction = (name, applicationId) => () => {
    switch (name) {
      case 'accept':
        return acceptApplication(applicationId, id);
      case 'decline':
        return declineApplication(applicationId, id);
      default:
        return console.log('action not defined');
    }
  };

  return (
    <>
      <FiltersControls
        perPageSelected={perPage}
        perPageOnChange={({ value }) => setPerPage(value)}
      />
      <Table tablebreakpoint="lg">
        <Table.Head>
          <Table.Row>
            <Table.Heading>{t('page.yc.applications.firstAndLastName')}</Table.Heading>
            <Table.Heading>{t('page.yc.applications.email')}</Table.Heading>
            <Table.Heading>{t('page.yc.applications.createdAt')}</Table.Heading>
            <Table.Heading>{t('page.yc.applications.cvAndLetter')}</Table.Heading>
            <Table.Heading />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {loading ? (
            <Table.Row>
              <Table.Cell colSpan={6}>
                <Spinner />
              </Table.Cell>
            </Table.Row>
          ) : null}
          {!loading && !applications.length ? (
            <Table.Row>
              <Table.Cell colSpan={6}>{t('global.noResultsFound')}</Table.Cell>
            </Table.Row>
          ) : null}
          {!loading && applications.length
            ? applications.map((application) => (
                <Table.Row key={application.id}>
                  <Table.Cell middle>
                    <Table.Label>{t('page.yc.applications.firstAndLastName')}:</Table.Label>
                    {`${application.name} ${application.surname}`}
                  </Table.Cell>
                  <Table.Cell middle>
                    <Table.Label>{t('page.yc.applications.email')}:</Table.Label>
                    {application.email}
                  </Table.Cell>
                  <Table.Cell middle>
                    <Table.Label>{t('page.yc.applications.createdAt')}:</Table.Label>
                    {application.updatedAt && moment(application.updatedAt).format('DD/MM/YYYY')}
                  </Table.Cell>
                  <Table.Cell middle>
                    <Table.Label>{t('page.yc.applications.cvAndLetter')}:</Table.Label>
                    <FlexColumn>
                      <ButtonAction
                        onClick={() => {}}
                        title={t('page.yc.applications.downloadCv')}
                        icon="fileUpload"
                        as="a"
                        target="_blank"
                        rel="nofollow noopener"
                        href={application.cv}
                        disabled={!application.cv}
                      />
                      <ButtonAction
                        onClick={() => {}}
                        title={t('page.yc.applications.downloadLetter')}
                        icon="fileUpload"
                        as="a"
                        target="_blank"
                        rel="nofollow noopener"
                        href={application.letter}
                        disabled={!application.letter}
                      />
                    </FlexColumn>
                  </Table.Cell>
                  <Table.Cell middle>
                    <FlexColumn>
                      <ButtonAction
                        onClick={() => setAcceptConfirmModal(application.id)}
                        title={t('global.accept')}
                        icon="checkCircle"
                      />
                      <ButtonAction
                        onClick={() => setDeclineConfirmModal(application.id)}
                        title={t('global.reject')}
                        icon="closeCircle"
                      />
                    </FlexColumn>
                  </Table.Cell>
                </Table.Row>
              ))
            : null}
        </Table.Body>
      </Table>
      <Pagination styled currentPage={page} totalPages={pages} onChange={setPage} />
      <Modal
        heading={t('page.yc.applications.modalAccept')}
        description={t('page.yc.applications.modalAcceptDesc')}
        isOpen={!!acceptConfirmModal}
        confirm={() => handleAction('accept', acceptConfirmModal)() && setAcceptConfirmModal(null)}
        cancel={() => setAcceptConfirmModal(null)}
      />
      <Modal
        heading={t('page.yc.applications.modalDecline')}
        description={t('page.yc.applications.modalDeclineDesc')}
        isOpen={!!declineConfirmModal}
        confirm={() =>
          handleAction('decline', declineConfirmModal)() && setDeclineConfirmModal(null)
        }
        cancel={() => setDeclineConfirmModal(null)}
      />
    </>
  );
};

OnHold.propTypes = {
  applications: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  page: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  loadApplications: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  acceptApplication: PropTypes.func.isRequired,
  declineApplication: PropTypes.func.isRequired,
};

const mapStateToProps = ({ ipma, loading }) => ({
  applications: ipma.applications.list.items,
  page: ipma.applications.list.page,
  pages: ipma.applications.list.pages,
  perPage: ipma.applications.list.perPage,
  loading: isLoading(loading, 'IPMA'),
});

const mapDispatchToProps = (dispatch) => ({
  loadApplications: (payload, id) => dispatch(loadIpmaProjectRoleApplicationsCreator(payload, id)),
  setPage: (payload) => dispatch(setIpmaProjectRoleApplicationPageCreator(payload)),
  setPerPage: (payload) => dispatch(setIpmaProjectRoleApplicationPerPageCreator(payload)),
  acceptApplication: (payload, roleId) =>
    dispatch(acceptIpmaProjectRoleApplicationCreator(payload, roleId)),
  declineApplication: (payload, roleId) =>
    dispatch(declineIpmaProjectRoleApplicationCreator(payload, roleId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OnHold);
